import React, { useState, useEffect, useRef } from 'react';
import { Camera, Mic, Video, Square, Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Loader,Crown } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import API_URL from '../config/API_URL';
import { BookViewer } from '../items/BookViewer';
const RecordingPage = () => {
  const [book, setBook] = useState(null);
  const [selectedKids, setSelectedKids] = useState([]);
  const [availableKids, setAvailableKids] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordingState, setRecordingState] = useState('idle');
  const [recordingType, setRecordingType] = useState('video_with_pdf');
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [uploadedRecordingId, setUploadedRecordingId] = useState(null);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);

  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  const [shareTokensRemaining, setShareTokensRemaining] = useState(0);
  const [requestingFamilyId, setRequestingFamilyId] = useState(null);

  const { showToast, ToastContainer } = useToast();
  
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const animationRef = useRef(null);
  const durationRef = useRef(0);

  
  const urlParams = new URLSearchParams(window.location.search);
  const bookId = urlParams.get('bookId');

  const isPremium = urlParams.get('premium') === 'true';
  const preselectedKidId = urlParams.get('childId');

  // Load PDF.js
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      setPdfJsLoaded(true); // Signal that it's ready
    };
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Fetch book details and load PDF
  useEffect(() => {
    if (bookId && pdfJsLoaded) {
      setPdfLoading(true);
      setPdfError(false);
      
      const premiumParam = isPremium ? '?premium=true' : '';
      fetch(`${API_URL}/api/books/${bookId}${premiumParam}`, {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(async (data) => {
          setBook(data);
          
          // Load PDF document
          if (data.pdf_url && window.pdfjsLib) {
            try {
              const loadingTask = window.pdfjsLib.getDocument(data.pdf_url);
              const pdf = await loadingTask.promise;
              setPdfDoc(pdf);
              setPdfLoading(false);
              console.log('✅ PDF loaded successfully!');
            } catch (err) {
              console.error('❌ Error loading PDF:', err);
              setPdfError(true);
              setPdfLoading(false);
            }
          } else {
            setPdfLoading(false);
          }
        })
        .catch(err => {
          console.error('Error fetching book:', err);
          setPdfError(true);
          setPdfLoading(false);
        });
    }
  }, [bookId, pdfJsLoaded, isPremium]);

  // Add a reload function
  const reloadPDF = async () => {
    if (!book?.pdf_url || !window.pdfjsLib) return;
    
    setPdfLoading(true);
    setPdfError(false);
    
    try {
      const loadingTask = window.pdfjsLib.getDocument(book.pdf_url);
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setPdfLoading(false);
      showToast('PDF reloaded successfully!');
      console.log('✅ PDF reloaded successfully!');
    } catch (err) {
      console.error('❌ Error reloading PDF:', err);
      setPdfError(true);
      setPdfLoading(false);
      showToast('Failed to reload PDF. Please refresh the page.');
    }
  };

  // Render PDF page to canvas when page changes
  useEffect(() => {
    if (pdfDoc && pdfCanvasRef.current && recordingType === 'video_with_pdf') {
      renderPDFPage(currentPage);
    }
  }, [pdfDoc, currentPage, recordingType]);

  const renderPDFPage = async (pageNum) => {
    if (!pdfDoc || !pdfCanvasRef.current || recordingType !== 'video_with_pdf') return;
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      const canvas = pdfCanvasRef.current;
      const context = canvas.getContext('2d');
      
      // Clear the canvas first
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      const viewport = page.getViewport({ scale: 1.5 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
    } catch (err) {
      console.error('Error rendering PDF page:', err);
    }
  };

  // Fetch available kids
  useEffect(() => {
    const fetchKidsWithSubscriptionInfo = async () => {
      try {
        // Get current user's subscription info
        const subRes = await fetch(`${API_URL}/api/stripe/subscription-status`, {
          credentials: 'include'
        });
        const subData = await subRes.json();
        console.log('🔍 RAW SUBSCRIPTION DATA:', subData);
        const userSubscription = subData.plan_type;
        console.log('🔍 USER SUBSCRIPTION:', userSubscription);

        setShareTokensRemaining(subData.share_tokens_remaining || 0);

        
        // Get all families
        const familiesRes = await fetch(`${API_URL}/api/family/my-families`, {
          credentials: 'include'
        });
        const families = await familiesRes.json();
        // const readerFamilies = families.filter(f => f.my_role === 'reader');
        const readerFamilies = families;
        console.log('🔍 READER FAMILIES:', readerFamilies);
        
        const allKids = [];
        
        for (const family of readerFamilies) {
          const detailRes = await fetch(`${API_URL}/api/family/${family.id}`, {
            credentials: 'include'
          });
          const detail = await detailRes.json();
          console.log(`🔍 FAMILY ${family.id} DETAILS:`, detail);
          
          if (detail.kids) {
            // Get family subscription status
            let familySubscriptionTier = 'free';
            
            // Check all family members for subscriptions
            if (detail.members) {
              for (const member of detail.members) {
                console.log(`🔍 CHECKING MEMBER:`, member);
                if (member.family_role === 'admin') {
                  try {
                    const memberSubRes = await fetch(`${API_URL}/api/stripe/user/${member.id}/status`, {
                      credentials: 'include'
                    });
                    const memberSubData = await memberSubRes.json();
                    console.log(`🔍 MEMBER ${member.id} SUBSCRIPTION:`, memberSubData);
                    if (memberSubData.subscription && memberSubData.subscription.status === 'active') {
                      familySubscriptionTier = memberSubData.subscription.tier;
                      console.log(`✅ FAMILY TIER SET TO:`, familySubscriptionTier);
                      break;
                    }
                  } catch (err) {
                    console.log('❌ Could not fetch member subscription:', err);
                  }
                }
              }
            }
            
            detail.kids.forEach(kid => {
              allKids.push({
                ...kid,
                familyName: detail.family_name,
                familyId: detail.id,
                familySubscriptionTier: familySubscriptionTier
              });
            });
          }
        }
        
        console.log('🔍 ALL KIDS BEFORE FILTERING:', allKids);
        console.log('🔍 IS PREMIUM?', isPremium);
        console.log('🔍 PRESELECTED KID ID:', preselectedKidId);
        
        // Filter kids based on premium story rules
        let filteredKids = allKids;
        
        if (isPremium) {
          const userTier = userSubscription;
          console.log('🔍 USER TIER FOR FILTERING:', userTier);
          const reqFamilyId = allKids.find(k => k.id === parseInt(preselectedKidId))?.familyId;
          setRequestingFamilyId(reqFamilyId);
          
          if (userTier === 'free') {
            // Free grandparent: only kids from the requesting family
            const requestingFamilyId = allKids.find(k => k.id === parseInt(preselectedKidId))?.familyId;
            
            console.log('🔍 REQUESTING FAMILY ID:', requestingFamilyId);
            filteredKids = allKids.filter(k => k.familyId === requestingFamilyId);
            console.log('✅ FREE TIER - FILTERED KIDS:', filteredKids);
          } else if (userTier === 'premium') {
            // Premium grandparent: only kids from premium or gold families
            filteredKids = allKids.filter(k => 
              k.familySubscriptionTier === 'premium' || k.familySubscriptionTier === 'gold'
            );
            console.log('✅ PREMIUM TIER - FILTERED KIDS:', filteredKids);
          } else if (userTier === 'gold') {
            // Gold grandparent: all kids (no filtering)
            filteredKids = allKids;
            console.log('✅ GOLD TIER - ALL KIDS:', filteredKids);
          }
        }
        
        console.log('🔍 FINAL FILTERED KIDS:', filteredKids);
        
        setAvailableKids(filteredKids);
        
        if (preselectedKidId) {
          const preselected = filteredKids.find(k => k.id === parseInt(preselectedKidId));
          console.log('🔍 PRESELECTED KID FOUND:', preselected);
          if (preselected) {
            setSelectedKids([preselected]);
          }
        }
      } catch (err) {
        console.error('❌ ERROR FETCHING KIDS:', err);
      }
    };
    
    fetchKidsWithSubscriptionInfo();
  }, [preselectedKidId, isPremium]);
  // Initialize webcam preview
  useEffect(() => {
    const initializeWebcam = async () => {
      if (recordingType !== 'audio_only') {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { width: 1280, height: 720 }, 
            audio: false
          });
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          
          streamRef.current = stream;
        } catch (err) {
          console.error('Error accessing webcam:', err);
        }
      }
    };

    initializeWebcam();

    return () => {
      if (streamRef.current && streamRef.current.getTracks) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [recordingType]);

  const setupRecording = async () => {
    try {
      let stream;
      
      if (recordingType === 'audio_only') {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
      } else if (recordingType === 'webcam_only') {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: 1280, height: 720 }, 
          audio: true 
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        
      } else if (recordingType === 'video_with_pdf') {
        // Get webcam with audio
        const webcamStream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: 1280, height: 720 }, 
          audio: true 
        });
        
        const canvas = canvasRef.current;
        canvas.width = 1920;
        canvas.height = 1080;
        const ctx = canvas.getContext('2d');
        
        const webcamVideo = document.createElement('video');
        webcamVideo.srcObject = webcamStream;
        webcamVideo.autoplay = true;
        webcamVideo.muted = true;
        await webcamVideo.play();
        
        streamRef.current = {
          webcamVideo,
          webcamStream
        };
        
        // Draw function that combines PDF canvas and webcam
        const draw = () => {
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, 1920, 1080);
          
          // Draw PDF on left half
          if (pdfCanvasRef.current) {
            const pdfCanvas = pdfCanvasRef.current;
            const scale = Math.min(960 / pdfCanvas.width, 1080 / pdfCanvas.height);
            const scaledWidth = pdfCanvas.width * scale;
            const scaledHeight = pdfCanvas.height * scale;
            const x = (960 - scaledWidth) / 2;
            const y = (1080 - scaledHeight) / 2;
            
            ctx.drawImage(pdfCanvas, x, y, scaledWidth, scaledHeight);
          }
          
          // Draw webcam on right half
          const webcamWidth = 960;
          const webcamHeight = 1080;
          const webcamX = 960;
          const webcamY = 0;
          
          ctx.save();
          ctx.translate(webcamX + webcamWidth / 2, webcamY + webcamHeight / 2);
          ctx.scale(-1, 1); // Mirror the webcam
          ctx.drawImage(
            webcamVideo,
            -webcamWidth / 2,
            -webcamHeight / 2,
            webcamWidth,
            webcamHeight
          );
          ctx.restore();
          
          animationRef.current = requestAnimationFrame(draw);
        };
        
        draw();
        
        const canvasStream = canvas.captureStream(30);
        const audioTrack = webcamStream.getAudioTracks()[0];
        if (audioTrack) {
          canvasStream.addTrack(audioTrack);
        }
        
        stream = canvasStream;
      }
            
      let mimeType = recordingType === 'audio_only' ? 'audio/webm' : 'video/webm';
      if (recordingType !== 'audio_only' && MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        mimeType = 'video/webm;codecs=vp8';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = handleRecordingComplete;
      mediaRecorderRef.current = mediaRecorder;
      
    } catch (err) {
      console.error('Error setting up recording:', err);
      showToast('Could not access camera/microphone. Please check permissions.');
    }
  };

  const startRecording = async () => {
    if (selectedKids.length === 0) {
      showToast('Please select at least one child to record for');
      return;
    }
    
    setShowConfirmModal(false);
    
    await setupRecording();
    
    if (mediaRecorderRef.current) {
      chunksRef.current = [];
      mediaRecorderRef.current.start(1000);
      setRecordingState('recording');
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          durationRef.current = newTime; // Keep ref in sync
          return newTime;
        });
      }, 1000);
    }
  };

  const calculateShareTokensNeeded = () => {
    console.log('CALCULATING SHARE TOKENS')
    console.log('isPremium:', isPremium)
    console.log('requestingFamilyId:', requestingFamilyId)
    
    if (!isPremium || !requestingFamilyId) return 0;
    
    return selectedKids.reduce((total, kid) => {
      // Same family = no tokens (always free)
      console.log('----------')
      console.log('----------',kid)
      console.log(kid.familyId)
      console.log(requestingFamilyId)
      if (kid.familyId === requestingFamilyId) return total;
      console.log('------------------')
      // Different family - check family tier
      console.log(kid)
      const familyTier = kid.familySubscriptionTier;
      console.log(familyTier)

      if (familyTier === 'free') {
        return total + 2; // 2 tokens for free family
      } else {
        return total + 1; // 1 token for premium or gold family
      }
    }, 0);
  };

  const shareTokensNeeded = calculateShareTokensNeeded();
  console.log(shareTokensNeeded)
  const pauseRecording = () => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.pause();
      setRecordingState('paused');
      clearInterval(timerRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && recordingState === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingState('recording');
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          durationRef.current = newTime; // Keep ref in sync
          return newTime;
        });
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecordingState('stopped');
      clearInterval(timerRef.current);
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  };

  const handleRecordingComplete = async () => {
    const blob = new Blob(chunksRef.current, { 
      type: recordingType === 'audio_only' ? 'audio/webm' : 'video/webm' 
    });
    
    if (streamRef.current) {
        if (recordingType === 'video_with_pdf') {
          streamRef.current.webcamStream.getTracks().forEach(track => track.stop());
        } else if (streamRef.current.getTracks) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      }
      
      // Use the ref value which has the final duration
      await uploadRecording(blob, durationRef.current);
  };
  const uploadRecording = async (blob, duration) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('recording', blob, `recording-${Date.now()}.webm`);
      formData.append('book_id', bookId);
      formData.append('recording_type', recordingType);
      formData.append('duration_seconds', duration);
      formData.append('kid_ids', JSON.stringify(selectedKids.map(k => k.id)));
      formData.append('is_premium', isPremium);
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(progress));
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          setUploadedRecordingId(response.recording_id);
          showToast('Recording uploaded successfully!');
          setShowSaveConfirmModal(true); // Show confirmation modal instead of redirecting
        } else {
          showToast('Upload failed. Please try again.');
        }
        setIsUploading(false);
      });
      
      xhr.addEventListener('error', () => {
        showToast('Upload error. Please try again.');
        setIsUploading(false);
      });
      
      xhr.open('POST', `${API_URL}/api/recording/upload`, true);
      xhr.withCredentials = true;
      xhr.send(formData);
      
    } catch (err) {
      console.error('Upload error:', err);
      showToast('Failed to upload recording');
      setIsUploading(false);
    }
  };

  const handleSaveAndContinue = () => {
    window.location.href = '/grandparent-dashboard';
  };

  const handleDeleteAndRerecord = async () => {
    if (!uploadedRecordingId) return;
    
    setShowSaveConfirmModal(false);
    setIsUploading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/recording/delete-upload/${uploadedRecordingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        showToast('Recording deleted. Ready to record again.');
        setUploadedRecordingId(null);
        resetRecording();
      } else {
        showToast('Failed to delete recording. Please try again.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Failed to delete recording');
    } finally {
      setIsUploading(false);
    }
  };

  const resetRecording = () => {
    setRecordingState('idle');
    setRecordingTime(0);
    durationRef.current = 0;
    chunksRef.current = [];
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    if (streamRef.current) {
      if (recordingType === 'video_with_pdf' && streamRef.current.webcamStream) {
        streamRef.current.webcamStream.getTracks().forEach(track => track.stop());
      } else if (streamRef.current.getTracks) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
    
    streamRef.current = null;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleKidSelection = (kid) => {
    setSelectedKids(prev => {
      const isSelected = prev.find(k => k.id === kid.id);
      if (isSelected) {
        return prev.filter(k => k.id !== kid.id);
      } else {
        return [...prev, kid];
      }
    });
  };

  if (!book) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>Loading book...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' }}>
      {/* Header */}
      <ToastContainer />
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '1rem 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button 
            onClick={() => window.location.href = '/grandparent-dashboard'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
          >
            <ChevronLeft size={24} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>{book.title}</h1>
              {isPremium && (
                <div style={{
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  fontSize: '0.625rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <Crown size={12} />
                  PREMIUM
                </div>
              )}
            </div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>by {book.author}</p>
          </div>
        </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {recordingState !== 'idle' && (
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: 'bold', 
                color: recordingState === 'recording' ? '#dc2626' : '#6b7280',
                fontFamily: 'monospace'
              }}>
                {formatTime(recordingTime)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div style={{ gridTemplateColumns: recordingType === 'video_with_pdf' ? '1fr' : '2fr 1fr', gap: '2rem' }}>
          {/* Left: Recording Area */}
          <div>
            {/* Side-by-side layout for video_with_pdf */}
            {recordingType === 'video_with_pdf' ? (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {/* PDF Viewer - Left Side */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '0.75rem', 
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}>
                  <div style={{ 
                    background: '#9333ea', 
                    color: 'white', 
                    padding: '0.75rem', 
                    fontWeight: '600',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <span>Story</span>
                    {(pdfError || !pdfDoc) && !pdfLoading && (
                      <button
                        onClick={reloadPDF}
                        style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <RotateCcw size={12} /> Reload
                      </button>
                    )}
                  </div>
                  <div style={{ 
                    height: '700px',
                    background: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    flexDirection: 'column',
                    gap: '1rem'
                  }}>
                    {pdfLoading ? (
                      <>
                        <Loader size={32} style={{ animation: 'spin 1s linear infinite', color: '#9333ea' }} />
                        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading PDF...</p>
                      </>
                    ) : pdfError ? (
                      <>
                        <div style={{ color: '#dc2626', fontSize: '2rem' }}>⚠️</div>
                        <p style={{ color: '#dc2626', fontSize: '0.875rem', textAlign: 'center' }}>
                          Failed to load PDF
                        </p>
                        <button
                          onClick={reloadPDF}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#9333ea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <RotateCcw size={16} /> Try Again
                        </button>
                      </>
                    ) : pdfDoc ? (
                      <canvas 
                        ref={pdfCanvasRef}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No PDF available</p>
                    )}
                  </div>
                </div>

                {/* Webcam - Right Side */}
                <div style={{ 
                  background: 'white', 
                  borderRadius: '0.75rem', 
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }}>
                  <div style={{ 
                    background: '#9333ea', 
                    color: 'white', 
                    padding: '0.75rem', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    Your Video
                  </div>
                  <div style={{ 
                    height: '700px',
                    background: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <video 
                      ref={videoRef}
                      autoPlay 
                      muted
                      playsInline
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover',
                        transform: 'scaleX(-1)'
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* Original PDF Viewer for other modes */
                <div style={{ marginBottom: '1rem' }}>
                <BookViewer 
                  pdfDoc={pdfDoc}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}

            {/* PDF Controls */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 1rem',
                  background: currentPage === 1 ? '#e5e7eb' : '#9333ea',
                  color: currentPage === 1 ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <ChevronLeft size={20} /> Previous
              </button>
              <span style={{ fontSize: '1rem', color: '#4b5563', fontWeight: '500' }}>
                Page {currentPage} {pdfDoc && `of ${pdfDoc.numPages}`}
              </span>
              <button
                onClick={() => pdfDoc && setCurrentPage(prev => Math.min(pdfDoc.numPages, prev + 1))}
                disabled={!pdfDoc || currentPage === pdfDoc?.numPages}
                style={{
                  padding: '0.5rem 1rem',
                  background: (!pdfDoc || currentPage === pdfDoc?.numPages) ? '#e5e7eb' : '#9333ea',
                  color: (!pdfDoc || currentPage === pdfDoc?.numPages) ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: (!pdfDoc || currentPage === pdfDoc?.numPages) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Next <ChevronRight size={20} />
              </button>
            </div>

            {/* Webcam Preview (only for webcam_only mode) */}
            {recordingType === 'webcam_only' && (
              <div style={{ 
                background: 'white', 
                borderRadius: '0.75rem', 
                padding: '1rem',
                marginBottom: '1rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>Camera Preview</h3>
                </div>
                <video 
                  ref={videoRef}
                  autoPlay 
                  muted
                  playsInline
                  style={{ 
                    width: '100%', 
                    maxHeight: '300px',
                    borderRadius: '0.5rem',
                    background: '#000'
                  }}
                />
              </div>
            )}

            {/* Hidden canvas for combining streams */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Recording Controls */}
            <div style={{ 
              background: 'white', 
              borderRadius: '0.75rem', 
              padding: '1.5rem',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }}>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                {recordingState === 'idle' && (
                  <button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={selectedKids.length === 0 || (isPremium && shareTokensNeeded > shareTokensRemaining)}
                    style={{
                      padding: '1rem 2rem',
                      background: (selectedKids.length > 0 && (!isPremium || shareTokensNeeded <= shareTokensRemaining)) ? '#dc2626' : '#e5e7eb',
                      color: (selectedKids.length > 0 && (!isPremium || shareTokensNeeded <= shareTokensRemaining)) ? 'white' : '#9ca3af',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      cursor: (selectedKids.length > 0 && (!isPremium || shareTokensNeeded <= shareTokensRemaining)) ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Video size={24} /> Start Recording
                  </button>
                )}

                {recordingState === 'recording' && (
                  <>
                    <button
                      onClick={pauseRecording}
                      style={{
                        padding: '1rem 2rem',
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Pause size={24} /> Pause
                    </button>
                    <button
                      onClick={stopRecording}
                      style={{
                        padding: '1rem 2rem',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Square size={24} /> Stop
                    </button>
                  </>
                )}

                {recordingState === 'paused' && (
                  <>
                    <button
                      onClick={resumeRecording}
                      style={{
                        padding: '1rem 2rem',
                        background: '#059669',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Play size={24} /> Resume
                    </button>
                    <button
                      onClick={stopRecording}
                      style={{
                        padding: '1rem 2rem',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Square size={24} /> Stop
                    </button>
                  </>
                )}

                {recordingState === 'stopped' && !isUploading && (
                  <button
                    onClick={resetRecording}
                    style={{
                      padding: '1rem 2rem',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <RotateCcw size={24} /> Record Again
                  </button>
                )}
              </div>

              {isUploading && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    <span style={{ fontSize: '1rem', color: '#4b5563' }}>Uploading... {uploadProgress}%</span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '8px', 
                    background: '#e5e7eb', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${uploadProgress}%`, 
                      height: '100%', 
                      background: '#9333ea',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Settings & Kid Selection (hidden in video_with_pdf mode) */}
          {recordingType !== 'video_with_pdf' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop:"1rem" }}>
              {/* Recording Type */}
              <div style={{ 
                background: 'white', 
                borderRadius: '0.75rem', 
                padding: '1.5rem',
                marginBottom: '1rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Recording Type</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {[
                    { value: 'video_with_pdf', label: 'Video with PDF', icon: Video },
                    { value: 'audio_only', label: 'Audio Only', icon: Mic },
                    { value: 'webcam_only', label: 'Webcam Only', icon: Camera }
                  ].map(({ value, label, icon: Icon }) => (
                    <label
                      key={value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        border: `2px solid ${recordingType === value ? '#9333ea' : '#e5e7eb'}`,
                        borderRadius: '0.5rem',
                        cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed',
                        background: recordingType === value ? '#faf5ff' : 'white',
                        opacity: recordingState === 'idle' ? 1 : 0.5
                      }}
                    >
                      <input
                        type="radio"
                        name="recordingType"
                        value={value}
                        checked={recordingType === value}
                        onChange={(e) => setRecordingType(e.target.value)}
                        disabled={recordingState !== 'idle'}
                        style={{ cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed' }}
                      />
                      <Icon size={20} color={recordingType === value ? '#9333ea' : '#6b7280'} />
                      <span style={{ fontWeight: recordingType === value ? '600' : '400' }}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Kid Selection */}
              <div style={{ 
                background: 'white', 
                borderRadius: '0.75rem', 
                padding: '1.5rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                  Recording For
                  {selectedKids.length > 0 && (
                    <span style={{ color: '#9333ea', marginLeft: '0.5rem' }}>
                      ({selectedKids.length} selected)
                    </span>
                  )}
                  {isPremium && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '400', 
                      marginTop: '0.5rem',
                      color: shareTokensNeeded > shareTokensRemaining ? '#dc2626' : '#6b7280'
                    }}>
                      Share tokens: {shareTokensRemaining} available
                      {shareTokensNeeded > 0 && (
                        <span style={{ marginLeft: '0.5rem' }}>
                          (using {shareTokensNeeded} for this recording)
                        </span>
                      )}
                    </div>
                  )}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                  {availableKids.length === 0 ? (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No kids available. Connect with families first.</p>
                  ) : (
                    availableKids.map(kid => {
                    const isSelected = selectedKids.find(k => k.id === kid.id);
                    const isShareToken = isPremium && kid.familyId !== requestingFamilyId;
                    
                    return (
                      <label
                        key={kid.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          border: `2px solid ${isSelected ? '#9333ea' : '#e5e7eb'}`,
                          borderRadius: '0.5rem',
                          cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed',
                          background: isSelected ? '#faf5ff' : 'white',
                          opacity: recordingState === 'idle' ? 1 : 0.5,
                          position: 'relative'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => toggleKidSelection(kid)}
                          disabled={recordingState !== 'idle'}
                          style={{ cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>{kid.first_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{kid.familyName}</div>
                        </div>
                        {isShareToken && (
                          <div style={{
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            color: '#f59e0b',
                            background: '#fef3c7',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem'
                          }}>
                            {kid.familySubscriptionTier === 'free' ? '2 SHARE TOKENS' : '1 SHARE TOKEN'}
                          </div>
                        )}
                      </label>
                    );
                  })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Settings panel for video_with_pdf mode - show below */}
          {recordingType === 'video_with_pdf' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
              {/* Recording Type */}
              <div style={{ 
                background: 'white', 
                borderRadius: '0.75rem', 
                padding: '1.5rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Recording Type</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {[
                    { value: 'video_with_pdf', label: 'Video with PDF', icon: Video },
                    { value: 'audio_only', label: 'Audio Only', icon: Mic },
                    { value: 'webcam_only', label: 'Webcam Only', icon: Camera }
                  ].map(({ value, label, icon: Icon }) => (
                    <label
                      key={value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        border: `2px solid ${recordingType === value ? '#9333ea' : '#e5e7eb'}`,
                        borderRadius: '0.5rem',
                        cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed',
                        background: recordingType === value ? '#faf5ff' : 'white',
                        opacity: recordingState === 'idle' ? 1 : 0.5
                      }}
                    >
                      <input
                        type="radio"
                        name="recordingType"
                        value={value}
                        checked={recordingType === value}
                        onChange={(e) => setRecordingType(e.target.value)}
                        disabled={recordingState !== 'idle'}
                        style={{ cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed' }}
                      />
                      <Icon size={20} color={recordingType === value ? '#9333ea' : '#6b7280'} />
                      <span style={{ fontWeight: recordingType === value ? '600' : '400' }}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Kid Selection */}
              <div style={{ 
                background: 'white', 
                borderRadius: '0.75rem', 
                padding: '1.5rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                  Recording For
                  {selectedKids.length > 0 && (
                    <span style={{ color: '#9333ea', marginLeft: '0.5rem' }}>
                      ({selectedKids.length} selected)
                    </span>
                  )}
                  {isPremium && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '400', 
                      marginTop: '0.5rem',
                      color: shareTokensNeeded > shareTokensRemaining ? '#dc2626' : '#6b7280'
                    }}>
                      Share tokens: {shareTokensRemaining} available
                      {shareTokensNeeded > 0 && (
                        <span style={{ marginLeft: '0.5rem' }}>
                          (using {shareTokensNeeded} for this recording)
                        </span>
                      )}
                    </div>
                  )}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '300px', overflowY: 'auto' }}>
                  {availableKids.length === 0 ? (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No kids available. Connect with families first.</p>
                  ) : (
                    availableKids.map(kid => {
                      const isSelected = selectedKids.find(k => k.id === kid.id);
                      const isShareToken = isPremium && kid.familyId !== requestingFamilyId;
                      
                      return (
                        <label
                          key={kid.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            border: `2px solid ${isSelected ? '#9333ea' : '#e5e7eb'}`,
                            borderRadius: '0.5rem',
                            cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed',
                            background: isSelected ? '#faf5ff' : 'white',
                            opacity: recordingState === 'idle' ? 1 : 0.5,
                            position: 'relative'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleKidSelection(kid)}
                            disabled={recordingState !== 'idle'}
                            style={{ cursor: recordingState === 'idle' ? 'pointer' : 'not-allowed' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#1f2937' }}>{kid.first_name}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{kid.familyName}</div>
                          </div>
                          {isShareToken && (
                            <div style={{
                              fontSize: '0.625rem',
                              fontWeight: '700',
                              color: '#f59e0b',
                              background: '#fef3c7',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem'
                            }}>
                              {kid.familySubscriptionTier === 'free' ? '2 SHARE TOKENS' : '1 SHARE TOKEN'}
                            </div>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showSaveConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#1f2937' }}>
              Recording Uploaded Successfully! 🎉
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              Your recording has been uploaded. Would you like to save it and continue, or delete it and record again?
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleDeleteAndRerecord}
                disabled={isUploading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <RotateCcw size={18} /> Delete & Re-record
              </button>
              <button
                onClick={handleSaveAndContinue}
                disabled={isUploading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  opacity: isUploading ? 0.5 : 1
                }}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '400px',
              boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#1f2937' }}>
                Ready to Record?
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                Your recording will start immediately after confirming.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#e5e7eb',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={startRecording}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Start Recording
                </button>
              </div>
            </div>
          </div>
        )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RecordingPage;